import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Smile, Frown, CloudRain } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import Header from "../components/Header";

const MOODS = { happy: { label: "Happy", Icon: Smile, cls: "text-[#2F855A]" }, okay: { label: "Okay", Icon: CloudRain, cls: "text-[#975A16]" }, sad: { label: "Sad", Icon: Frown, cls: "text-[#2B6CB0]" } };

export default function HandoverPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/handover", { params: { profile_id: pid, date } });
        setData(d);
      } catch (e) {
        toast.error(apiError(e));
        navigate("/app");
      }
    })();
  }, [pid, date, navigate]);

  const print = () => window.print();

  return (
    <div data-testid="handover-page" className="min-h-screen bg-background">
      <div className="print:hidden"><Header /></div>
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
            <ArrowLeft size={20} strokeWidth={2.5} /> Back
          </button>
          <div className="flex items-center gap-3">
            <input type="date" data-testid="handover-date" value={date} onChange={(e) => setDate(e.target.value)} className="min-h-[48px] rounded-2xl border-2 border-foreground/25 px-3 text-base focus:outline-none focus:border-primary" />
            <button data-testid="handover-print" onClick={print} className="min-h-[48px] px-5 rounded-full bg-primary text-white font-bold flex items-center gap-2">
              <Printer size={20} strokeWidth={2.5} /> Print
            </button>
          </div>
        </div>

        {!data ? (
          <p className="mt-10 text-lg text-muted-foreground">Loading…</p>
        ) : (
          <div data-testid="handover-report" className="mt-6 bg-white rounded-3xl border-2 border-foreground/10 p-8">
            <h1 className="text-3xl font-extrabold">Handover — {data.profile_name}</h1>
            <p className="mt-1 text-lg font-semibold text-muted-foreground">{new Date(data.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", dateStyle: undefined, day: "numeric", month: "long", year: "numeric" })}</p>

            {data.alerts?.length > 0 && (
              <div data-testid="handover-alerts" className="mt-6 space-y-3">
                {data.alerts.map((a, i) => (
                  <div
                    key={i}
                    data-testid={`handover-alert-${i}`}
                    className={`rounded-2xl border-2 p-4 text-lg font-bold ${
                      a.level === "warning" ? "bg-[#F56565]/10 border-[#F56565]/50 text-[#C53030]" : "bg-[#ECC94B]/15 border-[#ECC94B]/60 text-[#975A16]"
                    }`}
                  >
                    {a.level === "warning" ? "⚠ " : "ⓘ "}{a.text}
                  </div>
                ))}
              </div>
            )}

            <h2 className="mt-8 text-xl font-extrabold">Mood</h2>
            {data.mood ? (
              <p data-testid="handover-mood" className={`mt-2 text-lg font-extrabold flex items-center gap-2 ${MOODS[data.mood].cls}`}>
                {(() => { const M = MOODS[data.mood].Icon; return <M size={28} strokeWidth={2.5} />; })()} {MOODS[data.mood].label}
              </p>
            ) : (
              <p className="mt-2 text-lg text-muted-foreground font-semibold">Not logged today</p>
            )}

            <h2 className="mt-8 text-xl font-extrabold">Routines</h2>
            {data.routines.length === 0 ? (
              <p className="mt-2 text-lg text-muted-foreground font-semibold">No routines set up</p>
            ) : (
              <div className="mt-3 space-y-2">
                {data.routines.map((r, i) => (
                  <div key={i} data-testid={`handover-routine-${i}`} className="flex items-center gap-3 text-lg">
                    <IconRenderer name={r.icon} size={24} className="text-primary shrink-0" />
                    <span className="font-bold flex-1">{r.name}</span>
                    <span className={`font-extrabold ${r.total > 0 && r.done >= r.total ? "text-[#2F855A]" : r.done > 0 ? "text-[#975A16]" : "text-muted-foreground"}`}>
                      {r.done}/{r.total} steps
                    </span>
                  </div>
                ))}
              </div>
            )}

            <h2 className="mt-8 text-xl font-extrabold">Care notes</h2>
            {data.notes.length === 0 ? (
              <p className="mt-2 text-lg text-muted-foreground font-semibold">No notes today</p>
            ) : (
              <div className="mt-3 space-y-3">
                {data.notes.map((n, i) => (
                  <div key={i} data-testid={`handover-note-${i}`} className="border-2 border-foreground/10 rounded-2xl p-4">
                    <p className="text-lg leading-relaxed">{n.text}</p>
                    <p className="mt-1 text-sm font-semibold text-muted-foreground">
                      {n.author_name} · {new Date(n.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-10 text-sm font-semibold text-muted-foreground">Generated by RoutineBuddy</p>
          </div>
        )}
      </main>
    </div>
  );
}
